import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { OfficeService } from "../services";
import { CalendarEvent, EventUpdate } from "../models";
import { logger } from "../utils";

type UseOfficeItemReturnType = {
  /** Indicates that the Office library is ready to use */
  isReady: boolean;
  /** Indicates the event is loading */
  isLoading: boolean;
  /** Indicates there was an error with getting the current event data */
  isError: boolean;
  /** Indicates the event is being updated and in this process of being fetched  */
  isFetching: boolean;
  /** Indicates the error that happened when loading or updating the event */
  error: string | null;
  /** The current calendar event object */
  event: CalendarEvent | null;
  /**
   * This is used to access the Office API and should not be used directly
   */
  officeService: OfficeService;
  /**
   * Method to update the underlying event object.
   *
   * @param {EventUpdate} data the event data to update
   * @returns {Promise<CalendarEvent | undefined>} the updated event
   */
  updateEvent: (data: EventUpdate) => Promise<CalendarEvent | undefined>;
  /**
   * Method to update the attendees on the underlying event object.
   *
   * @param { "required" | "optional"} type the type of attendees to update
   * @param {string[]} emails the emails of the attendees to update
   * @description This will add the attendees to the event and update the event object

   * @returns {Promise<CalendarEvent | undefined>} the updated event
   */
  updateAttendees: (
    type: "required" | "optional",
    emails: string[]
  ) => Promise<CalendarEvent | undefined>;
};

const useOfficeItemInternal = (): UseOfficeItemReturnType => {
  const officeService = useMemo(() => new OfficeService(), []);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const intervalId = useRef<number | null>(null);
  const intervalErrorCount = useRef<number>(0);
  const MAX_ALLOWED_INTERVAL_ERRORS = 15;

  /**
   * used to update the event
   * @param data the event data to update
   * @returns the updated event
   */
  const updateEvent = useCallback(
    async (data: EventUpdate): Promise<CalendarEvent | undefined> => {
      // reset error states
      setIsError(false);
      setError(null);

      // update the event data
      await officeService.saveEvent(data);

      // toggle the fetching state
      setIsFetching(true);

      // fetch the new data
      return await officeService
        .event()
        .then((event) => {
          setEvent(event);
          setIsLoading(false);
          setIsFetching(false);
          return event;
        })
        .catch((err) => {
          setIsFetching(false);
          setError(err?.message ?? "Error fetching event");
          return undefined;
        });
    },
    [officeService.isReady]
  );

  const updateAttendees = useCallback(
    async (type: "required" | "optional", emails: string[]): Promise<CalendarEvent | undefined> => {
      // reset error states
      setIsError(false);
      setError(null);

      // update the event data
      await officeService.addAttendees(type, emails);

      // toggle the fetching state
      setIsFetching(true);

      // fetch the new data
      return await officeService
        .event()
        .then((event) => {
          setEvent(event);
          setIsLoading(false);
          setIsFetching(false);
          return event;
        })
        .catch((err) => {
          setIsFetching(false);
          setError(err?.message ?? "Error fetching event");
          return undefined;
        });
    },
    [officeService.isReady]
  );

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setIsError(false);
        setError(null);

        const evt = await officeService.event();
        if (!evt) throw new Error("No event found");

        setEvent(evt);
        setIsLoading(false);
        intervalErrorCount.current = 0;
      } catch (error) {
        setIsError(true);
        setIsLoading(false);
        setError((error as Error)?.message ?? "Error fetching event");
        intervalErrorCount.current += 1;
      }
    };

    Office.onReady(() => {
      // Set the Office service to ready
      setIsReady(true);
    });

    // Start the interval to fetch the event data every second
    intervalId.current = window.setInterval(() => {
      if (!officeService.isReady) return;
      if (intervalErrorCount.current > MAX_ALLOWED_INTERVAL_ERRORS) {
        logger.error("useOfficeItem: Max interval error count reached");
        if (intervalId.current) {
          window.clearInterval(intervalId.current);
          intervalId.current = null;
        }
        return;
      }

      fetchEventData();
    }, 1000);

    return () => {
      if (intervalId.current) {
        window.clearInterval(intervalId.current);
        intervalId.current = null;
      }
    };
  }, []);

  return {
    event,
    isReady,
    isLoading,
    isError,
    isFetching,
    error,
    officeService,
    updateEvent,
    updateAttendees,
  };
};

// Create a context for sharing the Office item data
const OfficeItemContext = createContext<UseOfficeItemReturnType | undefined>(undefined);

type OfficeItemProviderProps = { children: React.ReactNode };

// Provider to wrap the app and supply shared Office item state
export const OfficeItemProvider: React.FC<OfficeItemProviderProps> = ({ children }) => {
  const officeItem = useOfficeItemInternal();

  return <OfficeItemContext.Provider value={officeItem}>{children}</OfficeItemContext.Provider>;
};

// Consumer hook that components will use to access the shared state
export function useOfficeItem(): UseOfficeItemReturnType {
  const context = useContext(OfficeItemContext);
  if (!context) {
    throw new Error("useOfficeItem must be used within an OfficeItemProvider");
  }
  return context;
}
